import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { isNotEmpty } from "@/utils/string_utils"
import { env } from "next-runtime-env"

export const ACCEPTED_FILE_TYPES = [
  "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/json",
  "text/markdown",
  "application/pdf",
  "text/plain"
].join(",")

export const ACCEPTED_FILE_EXTENSIONS = [
  ".csv",
  // ".docx",
  ".json",
  ".md",
  ".markdown",
  // ".pdf",
  ".txt"
].join(",")

export const ACCEPTED_FILES: string = [
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_EXTENSIONS
].join(",")

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getApiUrl(path: string): string {
	if (typeof window === 'undefined') {
		// Running on the server, use full URL for server-side API calls
		return `${getPublicURL()}/api${path}`
	}
	else {
		// Running on the client, use relative URL
		return `/api${path}`
	}
}

export async function fetchData(input: string, init?: RequestInit) {
	const apiUrl = getApiUrl(input.toString())
	const response = await fetch(apiUrl, init)

	if (!response.ok) {
		throw new Error(`API call to '${input}' failed with status ${response.status}, message:${response.statusText}`)
	}

	return await response.json()
}

export const isIntegerMultipleOf = (num: number, multiple: number): boolean => {
	return Number.isInteger(num) && num % multiple === 0
}

export function isValidEmail(search:string): boolean {
	const regexp = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
	const matches:boolean = regexp.test(search)
	return matches
}

export const isValidUuid = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

/**
	Get the file type from a mime type or pre-resolved file type
 */
export function getFileType(mimeType: string) {
	const mimeParts = mimeType.split("/")

	if (mimeParts && mimeParts.length > 1) {
		let fileType = mimeParts[1]

		if (fileType.includes("vnd.adobe.pdf")) {
			fileType = "pdf"
		}
		else if (fileType.includes("vnd.adobe.pdf")) {
			fileType = "pdf"
		}
		else if (fileType.includes("vnd.openxmlformats-officedocument.wordprocessingml.document") ||
				fileType.includes("docx")) {
			fileType = "docx"
		}
		else if (fileType.includes("vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
				fileType.includes("xlsx")) {
			fileType = "xlsx"
		}
		return fileType
	}
	return mimeType
}

export async function resolveFileType(file: File) {
	const extension = file.name.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'csv':
      return 'text/csv'
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case 'json':
      return 'application/json'
    case 'md':
      return 'text/markdown'
    case 'pdf':
      return 'application/pdf'
    case 'txt':
      return 'text/plain'
    default:
			if (await isEmbeddable(file, file.type)) {
	      return 'text/plain'
			}
      return file.type // Return the original MIME type if extension is not recognized
  }
}

export function normalizeFileName(fileName: string) {
	const validFilename = fileName.replace(/[^a-z0-9.]/gi, "_").toLowerCase()
	const extension = fileName.split(".").pop()
	const lastDot = validFilename.lastIndexOf(".")
	const baseName = validFilename.substring(0, lastDot != -1 ? lastDot : validFilename.length)
	const maxBaseNameLength = 100 - (extension?.length || 0) - 1

	if (baseName.length > maxBaseNameLength) {
		return baseName.substring(0, maxBaseNameLength) + "." + extension
	} else {
		return baseName + "." + extension
	}
}

export function stripBom(buffer: Buffer): Buffer {
  if (buffer.subarray(0, 3).equals(Buffer.from([0xEF, 0xBB, 0xBF]))) {
    return buffer.subarray(3) // UTF-8 BOM
  }
  if (buffer.subarray(0, 2).equals(Buffer.from([0xFE, 0xFF]))) {
    return buffer.subarray(2) // UTF-16 BE BOM
  }
  if (buffer.subarray(0, 2).equals(Buffer.from([0xFF, 0xFE]))) {
    return buffer.subarray(2) // UTF-16 LE BOM
  }
  return buffer
}

export function isProbablyText(buffer: Buffer): boolean {
  const cleanBuffer = stripBom(buffer)
  const textChars = cleanBuffer.subarray(0, 8000).toString('utf8')
  const replacementCount = (textChars.match(/\uFFFD/g) || []).length
  return replacementCount < 5
}

export const isEmbeddable = async (file: File, fileType: string): Promise<boolean> => {
	if (ACCEPTED_FILE_TYPES.split(",").includes(fileType)) {
		return true
	}
	const buffer = Buffer.from(await file.arrayBuffer())
	return isProbablyText(buffer)
}

export const removeValue = <T>(
  arr: T[],
  valueToRemove: T,
  comparator?: (a: T, b: T) => boolean
): T[] => {
  return arr.filter(item => comparator ? !comparator(item, valueToRemove) : item !== valueToRemove)
}

type FallbackValue = string | (() => string)

export function getValueOrFallback(
  value: string | null | undefined,
  fallback: FallbackValue
): string {
  if (isNotEmpty(value)) {
    return value as string
  }

  if (typeof fallback === 'string') {
    return fallback
  }

  if (typeof fallback === 'function') {
    const result = fallback();
    if (typeof result === 'string') {
   	 return result
  	}
	}
	throw new Error('Invalid fallback value')
}

export function findNotEmpty(...inputs: (string | null | undefined)[]) {
	for (let index = 0; index < inputs.length; index++) {
		const element = inputs[index];
		if (isNotEmpty(element)) {
			return element!
		}
	}
  return null
}

export function getSingleValue(param: string | string[] | undefined): string | undefined {
	if (Array.isArray(param)) {
		return param[0]
	}
	return param
}

export function printObject(obj: unknown): string {
	const seen = new WeakSet()

	return JSON.stringify(
		obj,
		(_, value) => {
			if (typeof value === 'function') {
				return `[Function: ${value.name || 'anonymous'}]`
			}

			if (typeof value === 'object' && value !== null) {
				if (seen.has(value)) {
					return '[Circular]'
				}
				seen.add(value)
			}

			return value
		},
		2 // Indentation level for pretty-printing
	)
}

// Utility function to recursively remove undefined, null, and empty objects
export const cleanObject = (obj: any): any => {
  if (Array.isArray(obj)) { // Recursively clean each item in the array
    return obj
      .map(cleanObject)
      .filter(item => item !== undefined && item !== null)
  } else if (typeof obj === 'object' && obj !== null) { // Recursively clean the object
    return Object.entries(obj)
      .filter(([_, value]) => value !== undefined && value !== null && !(typeof value === 'object' && Object.keys(value).length === 0))
      .reduce((acc, [key, value]) => {
        acc[key] = cleanObject(value)
        return acc
      }, {} as Record<string, any>)
  }
  return obj
}

export const isEqual = (a: any, b: any): boolean => {
	if (a === b) return true // Check for strict equality

	if (typeof a !== typeof b) return false // Different types

	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false // Different lengths
		// Sort arrays to ignore order and compare each element
		return a.every(itemA => b.some(itemB => isEqual(itemA, itemB)))
	}

	if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
		const keysA = Object.keys(a)
		const keysB = Object.keys(b)

		if (keysA.length !== keysB.length) return false // Different number of keys

		return keysA.every(key => {
			if (!keysB.includes(key)) return false // Key not found in b
			return isEqual(a[key], b[key]) // Recursively check each field
		})
	}

	return false // Fallback for non-matching types
}

export const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: NodeJS.Timeout | null = null

	const debouncedFunction = (...args: any[]) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }

  debouncedFunction.cancel = () => {
    if (timeout) clearTimeout(timeout)
  }

  return debouncedFunction
}

export function convertToNumeric(value: string | null): number | null {
	if (value === null) return null
	const parsed = parseFloat(value)
	return isNaN(parsed) ? null : parsed
}

export function createReverseMap<T extends string>(map: Record<string, T>): Record<T, string> {
	const reverseMap = {} as Record<T, string>

	for (const [key, value] of Object.entries(map)) {
		if (!reverseMap[value]) {
			reverseMap[value] = key
		}
	}

	return reverseMap
}

// export async function resolveIdToValue <T>(id: string| null, map: Map<string, T>, fetchFn: (id: string) => Promise<T>) {
// 	if (!id) {
// 		return "unknown"
// 	}
// 	else {
// 		if (!map.has(id)) {
// 			const val:T = await fetchFn(id)
// 			map.set(id, val)
// 		}
// 		return map.get(id)!
// 	}
// }

export function getDefaultValue<T>(): T {
  if (typeof null as T === null) return null as T
  if (typeof 0 as T === 'number') return 0 as T
  if (typeof '' as T === 'string') return '' as T
  // Add more type checks as needed
  throw new Error('Unable to determine default value for type T')
}

export async function resolveIdToValue<T>(
  id: string | null,
  map: Map<string, Promise<T>>,
  fetchFn: (id: string) => Promise<T>,
	defaultValue: T
): Promise<T> {
  if (!id) {
    return defaultValue
  }

  if (!map.has(id)) {
    const promise = fetchFn(id).then(value => {
			if (value !== null) {
        map.set(id, Promise.resolve(value))
      }
      return value
    })
    map.set(id, promise)
  }

  return map.get(id)!
}

export function getRelatedValue<T extends object>(value: string | T | null | undefined, fieldName: keyof T): string | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    return value
  }

  // Assuming the value is an object and has the specified field
  if (fieldName in value) {
    return value[fieldName] as string
  }

  // If none of the above conditions are met, you can handle it accordingly
  throw new Error('Invalid value type or field name')
}

export function errorToText(error: unknown) {
  if (error instanceof Error) {
		let actualMessage: string

		try {
			const data = JSON.parse(error.message)
			// If parsed, access the inner message
			actualMessage = data.message
		}
		catch {
			actualMessage = error.message
		}

    return actualMessage
  }
	else {
		return printObject(error) // Convert to string in case it's not an object
  }
}

// Replace block-level LaTeX delimiters \[ \] with $$ $$
// from: https://github.com/remarkjs/react-markdown/issues/785#issuecomment-1966495891
export const preprocessLaTeX = (content: string) => {
  const blockProcessedContent = content?.replace(
    /\\\[(.*?)\\\]/gs,
    (_, equation) => `$$${equation}$$`,
  )

  // Replace inline LaTeX delimiters \( \) with $ $
  const inlineProcessedContent = blockProcessedContent?.replace(  //added ? since got undefined
    /\\\((.*?)\\\)/gs,
    (_, equation) => `$${equation}$`,
  )
  return inlineProcessedContent
}

export const getPublicURL = (path: string = '') => {  //taken from lib/stripe for most part
  // Check if NEXT_PUBLIC_SITE_URL is set and non-empty. Set this to your site URL in production env.
	const pubSiteUrl = env("NEXT_PUBLIC_SITE_URL")
  let url =
    pubSiteUrl && pubSiteUrl.trim() !== ''
      ? pubSiteUrl
      : 'http://localhost:3000/' // If neither is set, default to localhost for local development.

  // Trim the URL and remove trailing slash if exists.
  url = url.replace(/\/+$/, '')
  // Make sure to include `https://` when not localhost.
  url = url.includes('http') ? url : `https://${url}`
  // Ensure path starts without a slash to avoid double slashes in the final URL.
  path = path.replace(/^\/+/, '')

  // Concatenate the URL and the path.
  return path ? `${url}/${path}` : url
}
